/**
 * Import Feature Types
 * Types for CSV import, validation, and conflict resolution
 */

import type { AuditHistoryEntry } from './note';

// ============================================================================
// Conflict Resolution Types
// ============================================================================

export type ImportConflictType =
  | 'duplicate_contract'
  | 'sealed_record'
  | 'unknown_user'
  | 'new_reference_data'
  | 'supplier_tier_change'
  | 'contract_termination';

export type DuplicateAction = 'update' | 'skip' | 'create_new';
export type SealedRecordAction = 'skip_all' | 'update_metadata_only' | 'force_update';
export type UnknownUserAction = 'skip_user' | 'skip_contract' | 'assign_default' | 'create_contact';
export type ReferenceDataAction = 'auto_add' | 'reject';
export type TerminationAction = 'expire_assessments' | 'keep_assessments';

export interface ConflictResolutionConfig {
  id?: number;

  // Duplicate handling
  duplicate_contract_action: DuplicateAction;

  // Sealed record handling
  sealed_record_action: SealedRecordAction;
  sealed_critical_fields: string[];
  sealed_metadata_fields: string[];

  // Unknown user handling
  unknown_user_action: UnknownUserAction;
  default_contract_owner_id?: number;
  default_contract_manager_id?: number;

  // Reference data handling
  new_reference_data_action: ReferenceDataAction;

  // Termination handling
  termination_action: TerminationAction;

  // Metadata
  updated_at?: string;
  updated_by?: number;
}

// Default configuration
export const DEFAULT_CONFLICT_CONFIG: ConflictResolutionConfig = {
  duplicate_contract_action: 'update',
  sealed_record_action: 'update_metadata_only',
  sealed_critical_fields: [
    'tier',
    'category',
    'contract_type',
    'supplier_number',
    'contract_scope',
    'start_date',
    'end_date',
    'amount'
  ],
  sealed_metadata_fields: [
    'description',
    'cost_center',
    'parent_agreement_number',
    'cms_contract_status',
    'contract_owner_email',
    'contract_manager_email',
    'additional_contract_data_1',
    'additional_contract_data_2',
    'additional_contract_data_3'
  ],
  unknown_user_action: 'skip_user',
  new_reference_data_action: 'auto_add',
  termination_action: 'expire_assessments'
};

// ============================================================================
// Import Row Types
// ============================================================================

export type RowStatus = 'valid' | 'warning' | 'error' | 'system_critical_error' | 'conflict';
export type RowAction = 'create' | 'update' | 'skip';

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ImportConflict {
  type: ImportConflictType;
  field?: string;
  message: string;
  existingValue?: string;
  newValue?: string;
  suggestedAction: string;
  selectedAction?: string;
}

export interface ContractImportData {
  contract_number: string;
  cms_contract_status: string;
  supplier_number: string;
  supplier_name: string;
  tier: string;
  additional_supplier_data_1?: string;
  additional_supplier_data_2?: string;
  contract_name: string;
  contract_type: string;
  start_date: string;
  end_date?: string;
  description?: string;
  amount?: number;
  category: string;
  cost_center?: string;
  parent_agreement_number?: string;
  cmg_division?: string;
  contract_owner_email?: string;
  contract_manager_email?: string;
  contract_scope: string;
  additional_contract_data_1?: string;
  additional_contract_data_2?: string;
  additional_contract_data_3?: string;
}

export interface ImportRow {
  rowNumber: number;
  data: ContractImportData;
  status: RowStatus;
  action: RowAction;
  actionReason?: string;
  conflicts: ImportConflict[];
  errors: ValidationError[];
  warnings: ValidationError[];
  // Conflict resolution flags
  assignDefaultOwner?: boolean;
  defaultOwnerId?: number;
  assignDefaultManager?: boolean;
  defaultManagerId?: number;
  terminateAssessments?: boolean;
  createUnknownContacts?: boolean;
}

// ============================================================================
// Import Preview Types
// ============================================================================

export interface ImportSummary {
  totalRows: number;
  validCount: number;
  warningCount: number;
  errorCount: number;
  conflictCount: number;
  createCount: number;
  updateCount: number;
  skipCount: number;
}

export interface NewReferenceData {
  type: 'cms_contract_status' | 'contract_type' | 'category';
  values: string[];
}

export interface ImportPreview {
  summary: ImportSummary;
  rows: ImportRow[];
  newSuppliers: { number: string; name: string; tier: string }[];
  newReferenceData: NewReferenceData[];
  appliedConfig: ConflictResolutionConfig;
  // File info for import history
  fileName: string;
  fileSize: number;
}

// ============================================================================
// Import History Types
// ============================================================================

export type ImportType = 'contacts' | 'contracts';

export type ImportStatus = 'Processing' | 'Processed' | 'Processed with Errors' | 'Failed' | 'Completed';

export interface ImportHistoryRecord {
  id: number;
  import_type: string;
  file_name: string;
  file_size: number;
  record_count: number;
  success_count: number;
  error_count: number;
  import_status: ImportStatus;
  import_notes: string | null;
  imported_by: number | null;
  imported_by_name: string;
  started_at: string;
  completed_at: string | null;
  job_type: string;
  is_completed: boolean;
}

export interface ImportErrorRecord {
  id: number;
  row_number: number;
  field_name: string | null;
  error_message: string;
  raw_data: string | null;
  created_at: string;
}

export interface ImportJobTypeTotals {
  all: number;
  scheduled: number;
  manual: number;
  on_demand: number;
}

export interface ImportHistoryResponse {
  records: ImportHistoryRecord[];
  total: number;
  limit: number;
  offset: number;
  jobTypeTotals: ImportJobTypeTotals;
}

export interface ImportHistoryFilters {
  search?: string;
  jobType?: string;
  /** 'pending' = Jobs Pending Review (not complete, Processing included) */
  completion?: string;
  columnFilters?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: string;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface ValidateImportRequest {
  file: File;
  config?: Partial<ConflictResolutionConfig>;
}

export interface ValidateImportResponse {
  success: boolean;
  preview: ImportPreview;
  errors?: string[];
}

export interface ValidateImportBackendResponse {
  totalRows: number;
  validation?: {
    validCount: number;
    warningCount: number;
    errorCount: number;
  };
  conflicts?: {
    conflictCount: number;
    createCount: number;
    updateCount: number;
    skipCount: number;
  };
  rows: Array<{
    rowNumber: number;
    data: Record<string, string>;
    status?: string;
    isValid?: boolean;
    action?: string;
    actionReason?: string;
    conflicts?: unknown[];
    errors?: ValidationError[];
    warnings?: ValidationError[];
    assignDefaultOwner?: boolean;
    defaultOwnerId?: number;
    assignDefaultManager?: boolean;
    defaultManagerId?: number;
    terminateAssessments?: boolean;
    createUnknownContacts?: boolean;
  }>;
  newSuppliers: Array<{ number: string; name: string; tier: string }>;
  newReferenceData: Record<string, string[]>;
  appliedConfig: ConflictResolutionConfig;
  fileName: string;
  fileSize: number;
}

export interface ExecuteImportRequest {
  preview: ImportPreview;
  config: ConflictResolutionConfig;
  rowOverrides?: { rowNumber: number; action: RowAction }[];
}

export interface ExecuteImportResponse {
  success: boolean;
  historyId: number;
  summary: {
    created: number;
    updated: number;
    skipped: number;
    errors: number;
  };
  errors?: { rowNumber: number; error: string }[];
}

export interface ImportError {
  rowNumber: number;
  error: string;
  details?: string;
}

export interface ScopeRule {
  id: number;
  field: string;
  operator: string;
  value: string;
  scope: 'in_scope' | 'out_of_scope';
  created_at?: string;
  updated_at?: string;
}

export interface ScopeRulesResponse {
  rules: ScopeRule[];
  total: number;
}

export interface ScopeSettingsResponse {
  global_scope_calculation: boolean;
}

export interface ScopeRecalculationRunResponse {
  id: number;
  status: string;
  trigger: string;
  started_at: string;
  completed_at: string | null;
  total: number;
  updated: number;
  include_count: number;
  exclude_count: number;
  flagged: number;
  error_count: number;
  started_by: number | null;
}

export interface RecalculateScopesResponse extends ScopeRecalculationRunResponse {
  success: boolean;
  message: string;
}

export const SCOPE_RULES_HISTORY_PAGE_SIZE = 10;

export interface ScopeRulesHistoryParams {
  limit: number;
  offset: number;
}

export interface ScopeRulesHistoryResponse {
  records: AuditHistoryEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface PendingScopeContract {
  id: number;
  contract_number: string;
  name: string;
  assessment_scope: string;
  pending_assessment_scope: string;
  pending_scope_reason: string | null;
  pending_scope_trigger: string | null;
  pending_scope_at: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
}

export interface ScopeRuleUpdatePayload {
  id?: number;
  ruleType?: string;
  ruleValue?: string | null;
  includeInScope?: boolean;
  minThreshold?: number | null;
}

export interface TriggerJobResponse {
  success: boolean;
  message: string;
}
